import suburbSeedSql from "@/assets/suburb_postcode_council_map_20260317.sql?raw";

export type SuburbLookupRow = {
  Suburb: string;
  PostCode: number;
  Council?: string | null;
  State?: string | null;
  Source?: "api" | "fallback";
};

export type AddressSuburbMatch = {
  projectAddress: string;
  suburb: string;
  postCode: string;
  council: string;
  matchedFromAddress: boolean;
};

type AddressSuburbMatchInput = {
  address: unknown;
  suburb?: unknown;
  postCode?: unknown;
  council?: unknown;
};

const suburbSeedPattern = /\(\s*N'((?:[^']|'')*)'\s*,\s*N'((?:[^']|'')*)'\s*,\s*N'((?:[^']|'')*)'\s*,\s*(?:N'((?:[^']|'')*)'|NULL)\s*,/g;

let fallbackRowsCache: SuburbLookupRow[] | null = null;

const unescapeSqlString = (value: string) => value.replace(/''/g, "'").trim();

const normalizeWhitespace = (value: string) => value.replace(/\s+/g, " ").trim();

const normalizeAddressWords = (value: unknown) => (
  String(value ?? "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
);

const containsWholePhrase = (value: string, phrase: string) => (
  Boolean(value && phrase && ` ${value} `.includes(` ${phrase} `))
);

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const stripMatchedAddressLocality = (address: string, suburb: string, postCode: string) => {
  let streetAddress = normalizeWhitespace(address);
  if (postCode) {
    streetAddress = streetAddress.replace(new RegExp(`(?:[\\s,]+)${escapeRegExp(postCode)}\\s*$`, "i"), "");
  }
  streetAddress = streetAddress.replace(/(?:[\s,]+)(?:VIC|VICTORIA)\s*$/i, "");
  if (suburb) {
    const suburbPattern = suburb
      .split(/\s+/)
      .filter(Boolean)
      .map(escapeRegExp)
      .join("[\\s,]+");
    streetAddress = streetAddress.replace(new RegExp(`(?:[\\s,]+)${suburbPattern}\\s*$`, "i"), "");
  }
  return streetAddress.replace(/[\s,]+$/g, "").trim() || normalizeWhitespace(address);
};

export const normalizeSuburbLookupValue = (value: unknown) => (
  normalizeWhitespace(String(value ?? "")).toLowerCase()
);

export const normalizeLookupPostCode = (value: unknown) => {
  const digits = String(value ?? "").replace(/\D/g, "");
  if (!digits || digits.length < 3 || digits.length > 4) {
    return "";
  }

  const parsed = Number.parseInt(digits, 10);
  return Number.isFinite(parsed) ? String(parsed) : "";
};

function parseFallbackRows(): SuburbLookupRow[] {
  if (fallbackRowsCache) {
    return fallbackRowsCache;
  }

  const rows: SuburbLookupRow[] = [];
  const seen = new Set<string>();

  for (const match of suburbSeedSql.matchAll(suburbSeedPattern)) {
    const suburb = normalizeWhitespace(unescapeSqlString(match[1] || ""));
    const postCodeText = normalizeLookupPostCode(match[2] || "");
    const state = normalizeWhitespace(unescapeSqlString(match[3] || ""));
    const council = match[4] ? normalizeWhitespace(unescapeSqlString(match[4])) : null;
    const postCode = Number.parseInt(postCodeText, 10);
    const key = `${normalizeSuburbLookupValue(suburb)}|${postCodeText}`;

    if (!suburb || !postCodeText || !Number.isFinite(postCode) || seen.has(key)) {
      continue;
    }

    seen.add(key);
    rows.push({
      Suburb: suburb,
      PostCode: postCode,
      Council: council,
      State: state || null,
      Source: "fallback",
    });
  }

  fallbackRowsCache = rows.sort((left, right) => {
    const suburbCompare = left.Suburb.localeCompare(right.Suburb, "en-AU");
    if (suburbCompare !== 0) {
      return suburbCompare;
    }
    return left.PostCode - right.PostCode;
  });

  return fallbackRowsCache;
}

const buildSuburbMatchRank = (row: SuburbLookupRow, query: string) => {
  const suburb = normalizeSuburbLookupValue(row.Suburb);
  if (suburb === query) return 0;
  if (suburb.startsWith(query)) return 1;
  if (suburb.includes(query)) return 2;
  return 3;
};

const dedupeRows = (rows: SuburbLookupRow[]) => {
  const seen = new Set<string>();
  return rows.filter((row) => {
    const key = `${normalizeSuburbLookupValue(row.Suburb)}|${normalizeLookupPostCode(row.PostCode)}`;
    if (!row.Suburb || !normalizeLookupPostCode(row.PostCode) || seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
};

export const lookupFallbackSuburbs = (query: string, limit = 8): SuburbLookupRow[] => {
  const normalizedQuery = normalizeSuburbLookupValue(query);
  if (normalizedQuery.length < 2) {
    return [];
  }

  return parseFallbackRows()
    .filter((row) => buildSuburbMatchRank(row, normalizedQuery) < 3)
    .sort((left, right) => {
      const rankCompare = buildSuburbMatchRank(left, normalizedQuery) - buildSuburbMatchRank(right, normalizedQuery);
      if (rankCompare !== 0) {
        return rankCompare;
      }
      return left.Suburb.localeCompare(right.Suburb, "en-AU");
    })
    .slice(0, Math.max(1, limit));
};

export const lookupFallbackSuburbsByPostCode = (postCode: string | number, limit = 20): SuburbLookupRow[] => {
  const normalizedPostCode = normalizeLookupPostCode(postCode);
  if (!normalizedPostCode) {
    return [];
  }

  return parseFallbackRows()
    .filter((row) => normalizeLookupPostCode(row.PostCode) === normalizedPostCode)
    .sort((left, right) => left.Suburb.localeCompare(right.Suburb, "en-AU"))
    .slice(0, Math.max(1, limit));
};

export async function searchSuburbOptions(query: string, limit = 8): Promise<SuburbLookupRow[]> {
  const normalizedQuery = normalizeWhitespace(String(query ?? ""));
  if (normalizedQuery.length < 2) {
    return [];
  }

  const fallbackMatches = lookupFallbackSuburbs(normalizedQuery, limit);

  try {
    const { azureSqlService } = await import("@/services/azureSqlService.js");
    const apiMatches = await azureSqlService.searchSuburbs(normalizedQuery, limit);
    return dedupeRows([
      ...(Array.isArray(apiMatches) ? apiMatches.map((row) => ({ ...row, Source: "api" as const })) : []),
      ...fallbackMatches,
    ]).slice(0, Math.max(1, limit));
  } catch {
    return fallbackMatches;
  }
}

export async function searchSuburbOptionsByPostCode(postCode: string | number, limit = 20): Promise<SuburbLookupRow[]> {
  const normalizedPostCode = normalizeLookupPostCode(postCode);
  if (!normalizedPostCode) {
    return [];
  }

  const fallbackMatches = lookupFallbackSuburbsByPostCode(normalizedPostCode, limit);

  try {
    const { azureSqlService } = await import("@/services/azureSqlService.js");
    const apiMatches = await azureSqlService.lookupSuburbsByPostCode(normalizedPostCode, limit);
    return dedupeRows([
      ...(Array.isArray(apiMatches) ? apiMatches.map((row) => ({ ...row, Source: "api" as const })) : []),
      ...fallbackMatches,
    ]).slice(0, Math.max(1, limit));
  } catch {
    return fallbackMatches;
  }
}

export function matchAddressAgainstSuburbRows(
  input: AddressSuburbMatchInput,
  rows: SuburbLookupRow[]
): AddressSuburbMatch {
  const address = normalizeWhitespace(String(input.address ?? ""));
  const normalizedAddress = normalizeAddressWords(address);
  const explicitSuburb = normalizeWhitespace(String(input.suburb ?? ""));
  const normalizedExplicitSuburb = normalizeSuburbLookupValue(explicitSuburb);
  const trailingPostCode = address.match(/\b(\d{4})\b\s*$/)?.[1] || "";
  const explicitPostCode = normalizeLookupPostCode(input.postCode);
  const desiredPostCode = explicitPostCode || normalizeLookupPostCode(trailingPostCode);
  const dedupedRows = dedupeRows(rows);

  const addressMatches = dedupedRows
    .filter((row) => containsWholePhrase(normalizedAddress, normalizeAddressWords(row.Suburb)))
    .filter((row) => !desiredPostCode || normalizeLookupPostCode(row.PostCode) === desiredPostCode)
    .sort((left, right) => {
      const lengthCompare = normalizeAddressWords(right.Suburb).length - normalizeAddressWords(left.Suburb).length;
      if (lengthCompare !== 0) return lengthCompare;
      return left.Suburb.localeCompare(right.Suburb, "en-AU");
    });

  const explicitMatch = dedupedRows.find((row) => (
    normalizeSuburbLookupValue(row.Suburb) === normalizedExplicitSuburb
    && (!desiredPostCode || normalizeLookupPostCode(row.PostCode) === desiredPostCode)
  )) || dedupedRows.find((row) => normalizeSuburbLookupValue(row.Suburb) === normalizedExplicitSuburb);

  const matchedRow = addressMatches[0] || explicitMatch || null;
  const suburb = normalizeWhitespace(matchedRow?.Suburb || explicitSuburb);
  const postCode = normalizeLookupPostCode(matchedRow?.PostCode) || desiredPostCode;
  const council = normalizeWhitespace(String(matchedRow?.Council || input.council || ""));
  const matchedFromAddress = Boolean(addressMatches[0]);

  return {
    projectAddress: matchedFromAddress
      ? stripMatchedAddressLocality(address, suburb, postCode)
      : address,
    suburb,
    postCode,
    council,
    matchedFromAddress,
  };
}

export async function matchAddressToSuburbList(input: AddressSuburbMatchInput): Promise<AddressSuburbMatch> {
  const address = normalizeWhitespace(String(input.address ?? ""));
  const explicitSuburb = normalizeWhitespace(String(input.suburb ?? ""));
  const normalizedExplicitSuburb = normalizeSuburbLookupValue(explicitSuburb);
  const trailingPostCode = address.match(/\b(\d{4})\b\s*$/)?.[1] || "";
  const postCode = normalizeLookupPostCode(input.postCode) || normalizeLookupPostCode(trailingPostCode);
  const fallbackAddressRows = parseFallbackRows().filter((row) => (
    containsWholePhrase(normalizeAddressWords(address), normalizeAddressWords(row.Suburb))
  ));
  const fallbackRows = dedupeRows([
    ...fallbackAddressRows,
    ...(postCode ? lookupFallbackSuburbsByPostCode(postCode, 20) : []),
    ...(explicitSuburb ? lookupFallbackSuburbs(explicitSuburb, 20) : []),
  ]);
  const fallbackResult = matchAddressAgainstSuburbRows(input, fallbackRows);
  const hasExactFallbackSuburb = fallbackRows.some((row) => (
    normalizeSuburbLookupValue(row.Suburb) === normalizedExplicitSuburb
    && (!postCode || normalizeLookupPostCode(row.PostCode) === postCode)
  ));

  if (fallbackResult.matchedFromAddress || hasExactFallbackSuburb) {
    return fallbackResult;
  }

  const [postCodeRows, suburbRows] = await Promise.all([
    postCode ? searchSuburbOptionsByPostCode(postCode, 20) : Promise.resolve([]),
    explicitSuburb ? searchSuburbOptions(explicitSuburb, 20) : Promise.resolve([]),
  ]);

  return matchAddressAgainstSuburbRows(input, [
    ...postCodeRows,
    ...suburbRows,
    ...fallbackRows,
  ]);
}
